/**
 * border-canada slug — create-form input schema (shared transport layer).
 * @see docs/OPT_IN_ORDER_DOCUMENTS.md
 */
import { z } from "zod";
import { OrderDocumentSourceTypeSchema } from "~/shared/documents/order-document-source";

import { optionalCustomsTextSchema, optionalPositiveCustomsDecimalSchema } from "~/shared/documents/schemas/customs-input.schema";
import { DisplayUomOverrideSchema } from "~/shared/documents/schemas/document-display-uom.schema";
import { TypedSignatureInputSchema } from "~/shared/documents/schemas/typed-signature.schema";
import { DocumentShippableEntitySchema } from "~/utils/address-formatters";
import { EditorSchema, emptyEditorDoc } from "~/utils/types/rich-text";

export const BorderCanadianLineItemInputSchema = z.object({
  orderItemId: z.string().uuid(),
  description: z.string(),
  /** Prefill/UI display only — PDF generation formats from server order line totals. */
  valueUsd: z.string(),
  quantityKg: optionalPositiveCustomsDecimalSchema,
  tariffNumber: optionalCustomsTextSchema,
  duty: optionalCustomsTextSchema,
});

export const BorderCanadianDocumentInputSchema = z.object({
  /** Legacy transport name: identifies the source selected by sourceType. */
  orderId: z.string().uuid(),
  sourceType: OrderDocumentSourceTypeSchema.optional(),
  addressOverrides: z
    .object({
      billTo: DocumentShippableEntitySchema.optional(),
      vendor: DocumentShippableEntitySchema.optional(),
      shipFrom: DocumentShippableEntitySchema.optional(),
      deliverTo: DocumentShippableEntitySchema.optional(),
    })
    .optional(),
  /** Prefilled filing entity address block; broker may edit before create. PDF Bill To. */
  billTo: optionalCustomsTextSchema,
  /** Prefilled operating entity legal name; broker may edit before create. PDF Exporter label. */
  exporterLegalName: optionalCustomsTextSchema,
  approximateCrossingDate: optionalCustomsTextSchema,
  exportBorderCrossing: optionalCustomsTextSchema,
  importCrossingLocation: optionalCustomsTextSchema,
  containerType: optionalCustomsTextSchema,
  borderCrossingDetails: EditorSchema.default(emptyEditorDoc()),
  /** Layer 3 — prefilled from order instruction; editable in modal; not rendered on border broker PDF. */
  borderCrossingInstructions: EditorSchema.default(emptyEditorDoc()),
  vendorName: optionalCustomsTextSchema,
  vendorAddress: optionalCustomsTextSchema,
  shipFromName: optionalCustomsTextSchema,
  shipFromAddress: optionalCustomsTextSchema,
  /** Prefilled ship-to block; PDF uses this broker-visible snapshot. */
  deliverTo: optionalCustomsTextSchema,
  countryOfOrigin: optionalCustomsTextSchema,
  typedSignature: TypedSignatureInputSchema,
  lineItems: z.array(BorderCanadianLineItemInputSchema).min(1),
  displayUomOverride: DisplayUomOverrideSchema,
});

export type BorderCanadianDocumentInput = z.infer<typeof BorderCanadianDocumentInputSchema>;
